function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
import Log from '@deephaven/log';
var log = Log.module('@deephaven/jsapi-utils.WorkerVariablesStore');

/**
 * The current full list of variable definitions on a worker. Snapshot identity
 * is stable until the next field-update delta is applied, so consumers using
 * `useSyncExternalStore` will only re-render when the list actually changes.
 */

/** Default key used when a host exposes a single connection (e.g. DHC). */
export var DEFAULT_WORKER_KEY = 'default';

/**
 * Derive a stable string key identifying the worker that owns the variable
 * described by `descriptor`. Used by {@link WorkerVariablesStore} to dedup
 * subscriptions across consumers that target the same worker.
 *
 * The base `dh.ide.VariableDescriptor` type only carries `type`/`id`/`name`,
 * but DHE attaches routing fields (`querySerial`, `queryName`, `sessionId`).
 * Those are read defensively so this helper works in DHC too — where every
 * descriptor maps to {@link DEFAULT_WORKER_KEY}.
 */
export function getWorkerKey(descriptor) {
  if (descriptor == null) return DEFAULT_WORKER_KEY;
  var d = descriptor;
  if (typeof d.querySerial === 'string' && d.querySerial.length > 0) {
    return "q:".concat(d.querySerial);
  }
  if (typeof d.queryName === 'string' && d.queryName.length > 0) {
    return "qn:".concat(d.queryName);
  }
  if (typeof d.sessionId === 'string' && d.sessionId.length > 0) {
    return "s:".concat(d.sessionId);
  }
  return DEFAULT_WORKER_KEY;
}

/**
 * Resolves the IDE connection that owns the worker identified by `key`. Return
 * `null` if the worker is not currently reachable (e.g. query is stopped). The
 * store will retry on the next subscribe or {@link WorkerVariablesStore.invalidate}.
 */

/**
 * Identify a variable for delta matching. `dh.ide.VariableDefinition` declares
 * a non-optional `id`, but at runtime instances are frequently created as just
 * `{ name, type }` (e.g. field-update payloads and test fixtures), so `id` may
 * be absent. Fall back to `name`, then `title`, and return `undefined` when no
 * usable key exists so such items are never matched against a delta. Keys are
 * namespaced by source field so an `id` of `"x"` never collides with a `name`
 * of `"x"`.
 */
function getVariableKey(v) {
  if (typeof v.id === 'string' && v.id.length > 0) {
    return "id:".concat(v.id);
  }
  if (typeof v.name === 'string' && v.name.length > 0) {
    return "name:".concat(v.name);
  }
  if (typeof v.title === 'string' && v.title.length > 0) {
    return "title:".concat(v.title);
  }
  return undefined;
}

/**
 * A ref-counted store of worker variable lists, keyed by worker. Wraps
 * `IdeConnection.subscribeToFieldUpdates` so the underlying push subscription
 * is opened once per worker regardless of the number of React consumers.
 */

/**
 * Create a {@link WorkerVariablesStore} backed by `resolveConnection`. The
 * store is framework-free; pair it with a React provider/hook (see
 * `@deephaven/jsapi-bootstrap`'s `WorkerVariablesContext`/`useWorkerVariables`).
 */
export function createWorkerVariablesStore(resolveConnection) {
  var entries = new Map();

  /** Get the entry for `key`, creating an empty one if it doesn't exist. */
  function getOrCreate(key) {
    var entry = entries.get(key);
    if (entry == null) {
      entry = {
        list: null,
        listeners: new Set(),
        unsubscribeFieldUpdates: null,
        resolving: false,
        generation: 0
      };
      entries.set(key, entry);
    }
    return entry;
  }

  /** Invoke every listener on `entry`, isolating listener errors. */
  function notify(entry) {
    entry.listeners.forEach(listener => {
      try {
        listener();
      } catch (e) {
        log.error('WorkerVariables listener threw', e);
      }
    });
  }

  /** Close the field-updates subscription for `key`, if any. */
  function teardown(key) {
    var entry = entries.get(key);
    if ((entry === null || entry === void 0 ? void 0 : entry.unsubscribeFieldUpdates) != null) {
      try {
        entry.unsubscribeFieldUpdates();
      } catch (e) {
        log.warn('Error unsubscribing from field updates', e);
      }
      entry.unsubscribeFieldUpdates = null;
    }
  }

  /** Resolve the connection for `key` and open its field-updates subscription. */
  function start(_x) {
    return _start.apply(this, arguments);
  }
  /** Current list for `key`, or `null` if not yet resolved. */
  function _start() {
    _start = _asyncToGenerator(function* (key) {
      var entry = entries.get(key);
      if (entry == null || entry.resolving || entry.unsubscribeFieldUpdates != null) {
        return;
      }
      entry.resolving = true;
      var gen = entry.generation;
      try {
        var connection = yield resolveConnection(key);
        if (gen !== entry.generation || entry.listeners.size === 0 || entries.get(key) !== entry) {
          return;
        }
        if (connection == null) {
          log.debug('No connection available for worker', key);
          return;
        }
        // entry.list is replaced with a fresh array each delta so snapshot
        // identity is stable for `useSyncExternalStore` consumers.
        var unsubscribe = connection.subscribeToFieldUpdates(changes => {
          var _entry$list;
          if (gen !== entry.generation) return;
          var removedKeys = new Set(changes.removed.map(getVariableKey).filter(k => k != null));
          var updatedKeys = new Set(changes.updated.map(getVariableKey).filter(k => k != null));
          // Keep keyless items (they can't be matched) and items whose key was
          // not removed or updated; updated/created items are re-appended below.
          var next = ((_entry$list = entry.list) !== null && _entry$list !== void 0 ? _entry$list : []).filter(v => {
            var k = getVariableKey(v);
            return k == null || !removedKeys.has(k) && !updatedKeys.has(k);
          });
          next.push(...changes.updated, ...changes.created);
          entry.list = next;
          notify(entry);
        });
        entry.unsubscribeFieldUpdates = unsubscribe;
      } catch (e) {
        log.error('Failed to resolve worker connection', key, e);
      } finally {
        entry.resolving = false;
        // If `invalidate` ran while this resolve was in flight, it bumped
        // `generation` but couldn't start a fresh resolve (this one was still
        // marked `resolving`). The in-flight resolve above then bailed out as
        // stale, so kick off another resolve now that `resolving` is cleared —
        // otherwise the entry would be stuck with `list === null` and no
        // subscription until the next `invalidate`/subscribe. `gen` now equals
        // `entry.generation` when no invalidation occurred, so this never loops.
        if (entries.get(key) === entry && gen !== entry.generation && entry.unsubscribeFieldUpdates == null && entry.listeners.size > 0) {
          start(key).catch(() => undefined);
        }
      }
    });
    return _start.apply(this, arguments);
  }
  function snapshot(key) {
    var _entries$get$list, _entries$get;
    return (_entries$get$list = (_entries$get = entries.get(key)) === null || _entries$get === void 0 ? void 0 : _entries$get.list) !== null && _entries$get$list !== void 0 ? _entries$get$list : null;
  }

  /** Register `listener` for `key`, starting the subscription on first listener. */
  function subscribe(key, listener) {
    var entry = getOrCreate(key);
    entry.listeners.add(listener);
    if (entry.unsubscribeFieldUpdates == null && !entry.resolving) {
      // start handles its own errors; nothing to do on rejection
      start(key).catch(() => undefined);
    }
    return () => {
      entry.listeners.delete(listener);
      if (entry.listeners.size === 0) {
        teardown(key);
        entry.list = null;
        entries.delete(key);
      }
    };
  }

  /** Drop the cached list/subscription for `key` and re-resolve if observed. */
  function invalidate(key) {
    var entry = entries.get(key);
    if (entry == null) return;
    entry.generation += 1;
    teardown(key);
    entry.list = null;
    notify(entry);
    if (entry.listeners.size > 0) {
      // start handles its own errors; nothing to do on rejection
      start(key).catch(() => undefined);
    }
  }

  /** Tear down every entry and clear all state. */
  function destroy() {
    Array.from(entries.keys()).forEach(key => {
      teardown(key);
    });
    entries.clear();
  }
  return {
    snapshot,
    subscribe,
    invalidate,
    destroy
  };
}
//# sourceMappingURL=WorkerVariablesStore.js.map